import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { applyThemePreference, persistThemePreference, readThemePreference, isThemePreference, type ThemePreference } from './theme-runtime';
export type Theme = ThemePreference;
const valid = isThemePreference;
interface ThemeContextValue { theme: Theme; resolvedTheme: 'light' | 'dark'; setTheme: (theme: Theme) => void; toggleTheme: () => void }
const ThemeContext = createContext<ThemeContextValue | null>(null);
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, updateTheme] = useState<Theme>(readThemePreference);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  const resolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const change = () => setSystemDark(media.matches);
    change(); media.addEventListener('change', change);
    const sync = (event: StorageEvent) => { if (event.key === 'legea-theme' || event.key === null) updateTheme(valid(event.newValue) ? event.newValue : 'system'); };
    window.addEventListener('storage', sync);
    return () => { media.removeEventListener('change', change); window.removeEventListener('storage', sync); };
  }, []);
  useEffect(() => {
    applyThemePreference(theme);
  }, [theme, resolvedTheme]);
  const value = useMemo<ThemeContextValue>(() => {
    const setTheme = (next: Theme) => { updateTheme(next); persistThemePreference(next); };
    return { theme, resolvedTheme, setTheme, toggleTheme: () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark') };
  }, [theme, resolvedTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
// Co-located with the provider to keep the preference contract in one module.
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() { const context = useContext(ThemeContext); if (!context) throw new Error('useTheme requires ThemeProvider'); return context; }
